import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, Car } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import Breadcrumb from '../components/Breadcrumb';
import SortSelect, { sortProducts } from '../components/SortSelect';
import VehicleBrandLogo from '../components/VehicleBrandLogo';
import NewArrivalsSubscribe from '../components/NewArrivalsSubscribe';
import { productsAPI, cartAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { usePageMeta } from '../services/seo';
import { useT, i18n } from '../services/i18n';
import { catName } from '../services/catalogI18n';

const DAYS = [7, 30];

// « Nouveautés » : pièces ajoutées ces derniers jours, filtrables par catégorie et par véhicule sélectionné
const NewProductsPage = () => {
  const { t } = useT();
  const lang = i18n.get();
  const vehicle = useSelectedVehicle();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [cat, setCat] = useState(params.get('cat') || '');
  const [onlyVehicle, setOnlyVehicle] = useState(true);
  const [sort, setSort] = useState('newest');
  const [adding, setAdding] = useState(null);

  usePageMeta({ path: '/nouveautes', rawTitle: t('new.seoTitle'), description: t('new.seoDesc', { d: days }) });

  useEffect(() => {
    setLoading(true);
    productsAPI.getNew(days, vehicleStore.toParams(vehicle)).then(setData).catch(() => setData({ total: 0, categories: [], products: [], compatible_ids: null })).finally(() => setLoading(false));
  }, [days, vehicle]);

  const compatible = useMemo(() => (data?.compatible_ids ? new Set(data.compatible_ids) : null), [data]);
  const visible = useMemo(() => {
    if (!data) return [];
    let list = data.products;
    if (compatible && onlyVehicle) list = list.filter((p) => compatible.has(p.id));
    if (cat) list = list.filter((p) => p.category === cat);
    return sortProducts(list, sort);
  }, [data, compatible, onlyVehicle, cat, sort]);

  const addToCart = async (id) => {
    setAdding(id);
    try { await cartAPI.addItem(id); } finally { setAdding(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6" data-testid="new-products-page">
        <Breadcrumb items={[{ label: t('new.title') }]} />
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 flex items-center gap-2" data-testid="new-products-h1"><Sparkles className="text-orange-500" size={26} /> {t('new.h1', { d: days })}</h1>
            <p className="text-sm text-gray-600 mt-1" data-testid="new-products-sub">{t('new.sub')}</p>
          </div>
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1" data-testid="new-products-days">
            {DAYS.map((d) => (
              <button key={d} type="button" onClick={() => setDays(d)} data-testid={`new-products-days-${d}`} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${days === d ? 'bg-[#1e3a5f] text-white' : 'text-gray-700 hover:bg-gray-100'}`}>{t('new.days', { d })}</button>
            ))}
          </div>
        </div>

        {vehicle && (
          <div className="flex flex-wrap items-center gap-3 bg-white border border-orange-200 rounded-lg px-4 py-3 mb-4" data-testid="new-products-vehicle">
            <VehicleBrandLogo brand={vehicle.brand} model={vehicle.model} size={36} preferModel zoom testId="new-products-vehicle-logo" />
            <p className="text-sm text-gray-800 flex-1 min-w-0 truncate"><span className="font-semibold">{vehicle.brand} {vehicle.model}</span>{vehicle.engine?.name ? <span className="text-gray-500"> · {vehicle.engine.name}</span> : null}</p>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={onlyVehicle} onChange={(e) => setOnlyVehicle(e.target.checked)} className="accent-orange-500 w-4 h-4" data-testid="new-products-only-vehicle" />
              {t('new.onlyVehicle')} {compatible && <span className="text-xs text-gray-500">({compatible.size})</span>}
            </label>
          </div>
        )}
        {!vehicle && (
          <Link to="/#selecteur-vehicule" className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 mb-4 hover:bg-blue-100 transition-colors" data-testid="new-products-select-vehicle"><Car size={16} /> {t('new.selectVehicle')}</Link>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-orange-500" size={32} /></div>
        ) : (
          <>
            {data.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="new-products-categories">
                <button type="button" onClick={() => setCat('')} data-testid="new-products-cat-all" className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!cat ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'}`}>{t('new.allCats')} ({data.total})</button>
                {data.categories.map((c) => (
                  <button key={c.slug} type="button" onClick={() => setCat(cat === c.slug ? '' : c.slug)} data-testid={`new-products-cat-${c.slug}`} className={`inline-flex items-center gap-1.5 text-xs pl-1.5 pr-3 py-1 rounded-full border transition-colors ${cat === c.slug ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400 hover:text-orange-600'}`}>
                    {c.image && <img src={c.image} alt="" className="w-5 h-5 object-contain rounded-full bg-white" />}{catName(c, lang)} <span className={cat === c.slug ? 'text-orange-100' : 'text-gray-400'}>({c.count})</span>
                  </button>
                ))}
              </div>
            )}
            <NewArrivalsSubscribe category={cat || null} categoryName={cat ? catName(data.categories.find((c) => c.slug === cat), lang) : ''} />
            <div className="mb-4"><SortSelect value={sort} onChange={setSort} count={visible.length} /></div>
            {visible.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed p-10 text-center" data-testid="new-products-empty">
                <Sparkles className="mx-auto text-gray-300 mb-2" size={32} />
                <p className="text-gray-700 font-medium">{t('new.empty')}</p>
                {compatible && onlyVehicle && <button type="button" onClick={() => setOnlyVehicle(false)} className="mt-3 text-sm text-orange-600 hover:underline" data-testid="new-products-show-all">{t('new.showAll')}</button>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="new-products-grid">
                {visible.map((p) => <ProductCard key={p.id} product={p} compatible={compatible ? compatible.has(p.id) : false} onAddToCart={addToCart} isAdding={adding === p.id} isNew />)}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default NewProductsPage;
